import { useEffect, useState, useRef, LegacyRef } from 'react';
import { createPortal } from 'react-dom';

import dayjs from 'dayjs';
import { Paragraph1, Paragraph2, SmallText } from 'inpock-ui-react';

import { Image, Rating, Skeleton } from '@/components/common';
import { useCampaignReviewsQuery, useIntersectionObserver } from '@/hooks';
import { Review, ReviewSortCriteria } from '@/apis';
import { thumbnail } from '@/utils';

import { ReviewImageModal } from './Modal';
import * as S from './styled';

function EmptyCampaignReviews() {
  return (
    <S.ReviewEmpty>
      <div className="empty-icon" />
      <Paragraph1 fontWeight="bold" className="text-gray-400 mb-8">
        아직 등록된 리뷰가 없어요🥲{' '}
      </Paragraph1>
      <Paragraph2 className="text-gray-400">
        제품을 구매하고 첫 번째 리뷰어가 되어보세요!
        <br />
        구매를 고민하는 분들에게 큰 도움이 될 수 있어요.
      </Paragraph2>
    </S.ReviewEmpty>
  );
}

function ProductReviewImage({
  image,
  count,
  onClick,
}: {
  image: string;
  count: number;
  onClick?: () => void;
}) {
  return (
    <S.ReviewImageContainer onClick={onClick}>
      <Image lazy src={image} width={70} borderRadius={4} />
      {count > 1 && <S.ReviewImageCount>+{count}</S.ReviewImageCount>}
    </S.ReviewImageContainer>
  );
}

function ProductReview({ review }: { review: Review }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentState, setContentState] = useState({
    isLong: false,
    showMore: false,
    showImageModal: false,
  });
  const { username, contents, rating, medias, created_at } = review;

  const toggleShowMore = () => {
    setContentState((prev) => ({ ...prev, showMore: !prev.showMore }));
  };

  useEffect(() => {
    const [offsetHeight, scrollHeight] = [
      contentRef.current?.offsetHeight,
      contentRef.current?.scrollHeight,
    ];

    if (offsetHeight === undefined || scrollHeight === undefined) return;

    if (offsetHeight < scrollHeight) {
      setContentState({
        isLong: true,
        showMore: false,
        showImageModal: false,
      });
    }
  }, []);

  const onClickImage = () => {
    document.documentElement.style.overflow = 'hidden';
    setContentState((prev) => ({ ...prev, showImageModal: true }));
  };

  return (
    <S.ReviewContainer>
      <S.ReviewHeader>
        <S.ReviewHeaderInfo>
          <SmallText className="text-gray-600">{username}</SmallText>
          <Rating rating={rating} size={14} gap={1} readOnly />
        </S.ReviewHeaderInfo>
        <SmallText className="text-gray-600">{dayjs(created_at).format('YYYY.MM.DD')}</SmallText>
      </S.ReviewHeader>
      {review.additional_product_name ||
        (review.main_option_name && (
          <SmallText className="text-gray-500">
            {review.additional_product_name
              ? `추가옵션: ${review.additional_product_name} / ${review.additional_option_name}`
              : `옵션: ${review.main_option_name} / ${review.sub_option_name}`}
          </SmallText>
        ))}
      <S.ReviewBody>
        {medias.length > 0 && (
          <ProductReviewImage
            image={thumbnail.small(medias[0].url)}
            count={medias.length}
            onClick={onClickImage}
          />
        )}
        <div style={{ width: '100%' }}>
          <S.ReviewContent
            ref={contentRef}
            className={`${contentState.showMore ? 'show-more' : ''} ${
              contentState.isLong ? 'cursor-pointer' : ''
            }`}
            onClick={toggleShowMore}
          >
            {contents}
          </S.ReviewContent>
          {contentState.isLong && (
            <S.ReviewShowMore onClick={toggleShowMore}>
              <SmallText className="text-gray-400">
                {contentState.showMore ? '접기' : '...더보기'}
              </SmallText>
            </S.ReviewShowMore>
          )}
        </div>
      </S.ReviewBody>
      {contentState.showImageModal &&
        createPortal(
          <ReviewImageModal
            medias={medias}
            username={username}
            rating={rating}
            onClose={() => {
              document.documentElement.style.removeProperty('overflow');
              setContentState((prev) => ({ ...prev, showImageModal: false }));
            }}
          />,
          document.getElementById('review-image-modal') as HTMLDivElement,
        )}
    </S.ReviewContainer>
  );
}

function CampaignReviews({
  campaignId,
  criteria,
}: {
  campaignId: number;
  criteria?: ReviewSortCriteria;
}) {
  const [reviews, setReviews] = useState<Review[]>([]);
  console.log(reviews);
  const { value: sort_by } = criteria || { value: 'created_at' };

  const { data, fetchNextPage, hasNextPage, isFetching } = useCampaignReviewsQuery(
    campaignId as number,
    sort_by,
  );

  const { setTarget } = useIntersectionObserver({
    hasNextPage,
    fetchNextPage,
  });

  useEffect(() => {
    if (data) {
      const allReviews = data.pages.flatMap((page) => page.results);
      setReviews(allReviews);
    }
  }, [data]);

  return (
    <>
      {isFetching && hasNextPage === undefined ? (
        <Skeleton />
      ) : (
        <>
          {reviews.map((review) => (
            <ProductReview key={review.id} review={review} />
          ))}
          <div ref={setTarget as LegacyRef<HTMLDivElement>}></div>
        </>
      )}
    </>
  );
}

export { EmptyCampaignReviews, CampaignReviews };
